import { type ReactNode } from 'react';
import { Result } from 'antd';
import { usePermission } from '@hooks/usePermission';

interface PermissionGateProps {
  permissions?: string[];
  requireAll?: boolean;
  /** Requires an explicit Developer tools grant; not satisfied by `admin`. */
  requireDeveloper?: boolean;
  fallback?: ReactNode;
  children: ReactNode;
}

export function PermissionGate({
  permissions,
  requireAll = false,
  requireDeveloper = false,
  fallback,
  children,
}: PermissionGateProps) {
  const { can, canAny, canAll, isDeveloper } = usePermission();

  if (requireDeveloper && !isDeveloper) {
    return (
      fallback ?? (
        <Result
          status="403"
          title="Access Denied"
          subTitle="Developer tools access is required to view this page."
        />
      )
    );
  }

  if (!permissions || permissions.length === 0) {
    return <>{children}</>;
  }

  const hasAccess = requireAll
    ? canAll(permissions)
    : permissions.length === 1
      ? can(permissions[0])
      : canAny(permissions);

  if (!hasAccess) {
    return (
      fallback ?? (
        <Result
          status="403"
          title="Access Denied"
          subTitle="You do not have permission to view this page."
        />
      )
    );
  }

  return <>{children}</>;
}
